import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadSimple, MusicNote } from "@phosphor-icons/react";

const GENRES = ["Hip-Hop", "R&B", "Trap", "Pop", "Drill", "Afrobeats", "Electronic", "Rock", "Other"];
const inputCls = "bg-[#0F0F13] border-white/10 text-white rounded-none focus-visible:ring-2 focus-visible:ring-[#E1FF00] focus-visible:ring-offset-0 h-11";

export const SubmitDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("Hip-Hop");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setTitle(""); setFile(null); setGenre("Hip-Hop"); };

  const submit = async () => {
    if (!title.trim()) return toast.error("Track title is required");
    if (!file) return toast.error("Choose an mp3 or wav file");
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["mp3", "wav"].includes(ext)) return toast.error("Only mp3 or wav files are allowed");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("genre", genre);
      fd.append("file", file);
      await api.post("/tracks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Track added to the queue!");
      reset();
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit track");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="music-submit-form"
        className="bg-[#0F0F13] border border-white/10 text-white rounded-none max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-head font-bold text-2xl tracking-tight flex items-center gap-2">
            <MusicNote size={22} weight="fill" className="text-[#E1FF00]" /> Submit Your Track
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Upload an mp3 or wav to enter the live review queue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA]">Track Title</Label>
            <Input data-testid="submit-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name of the track" className={inputCls} />
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA]">Genre</Label>
            <div className="flex flex-wrap gap-2">
              {GENRES.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGenre(g)}
                  className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${genre === g ? "bg-[#B14AED] border-[#B14AED] text-white" : "bg-transparent border-white/10 text-[#A1A1AA] hover:border-white/30"}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA]">Audio File</Label>
            <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 bg-[#050505] py-10 cursor-pointer hover:border-[#E1FF00]/50 transition-colors">
              <UploadSimple size={30} className="text-[#52525B]" />
              <span className="text-sm text-[#A1A1AA]">{file ? file.name : "Click to choose an mp3 or wav"}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Full song · plays end-to-end on air</span>
              <input data-testid="submit-file-input" type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <Button
            data-testid="submit-track-button"
            onClick={submit}
            disabled={loading}
            className="w-full bg-[#E1FF00] hover:bg-[#CBE600] text-black font-bold rounded-none h-12 disabled:opacity-50"
          >
            {loading ? "Uploading…" : "Add To Queue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
